// Module configuration: field definitions drive tables + forms.
import {
  RadioTower, Cable, Boxes, GitFork, Split, Server, Router, Users,
  Waypoints, TriangleAlert, FileText, Zap, MapPin, CircleDot, Library, Package, HardHat, Warehouse, Cpu, Radar, Rows3, Network,
} from "lucide-react";

export const FIBER_COLORS = ["Czerwony", "Zielony", "Niebieski", "Żółty", "Fioletowy",
  "Biały", "Pomarańczowy", "Turkusowy", "Czarny", "Brązowy", "Różowy", "Szary"];

export const COLOR_HEX = {
  "Czerwony": "#EF4444", "Zielony": "#22C55E", "Niebieski": "#3B82F6", "Żółty": "#EAB308",
  "Fioletowy": "#A855F7", "Biały": "#F1F5F9", "Pomarańczowy": "#F97316", "Turkusowy": "#06B6D4",
  "Czarny": "#1E293B", "Brązowy": "#92400E", "Różowy": "#EC4899", "Szary": "#94A3B8",
};

// Kolejność kolorów wg standardów (musi być zgodna z backendem: core.py)
export const FIBER_STD_TIA = ["Niebieski", "Pomarańczowy", "Zielony", "Brązowy", "Szary", "Biały",
  "Czerwony", "Czarny", "Żółty", "Fioletowy", "Różowy", "Turkusowy"];
export const FIBER_STANDARDS_DEFAULT = [
  { name: "USA (TIA-598)", colors: FIBER_STD_TIA },
  { name: "PL/EU", colors: FIBER_COLORS },
];

// Lista kolorów dla nazwy standardu (fallback: PL/EU)
export function colorsForStandard(settings, name) {
  const stds = (settings && settings.fiber_standards) || FIBER_STANDARDS_DEFAULT;
  const st = stds.find((s) => s.name === name);
  return (st && st.colors) || FIBER_COLORS;
}

// Nazwa koloru włókna nr n wg standardu kabla (lub globalnego domyślnego)
export function fiberColorName(settings, cableOrStd, n) {
  const name = typeof cableOrStd === "string"
    ? cableOrStd
    : ((cableOrStd && cableOrStd.fiber_standard) || (settings && settings.default_fiber_standard));
  const cols = colorsForStandard(settings, name);
  return cols[(((n || 1) - 1) % cols.length + cols.length) % cols.length];
}

// point = has lat/lng marker; line = has geometry polyline
export const MODULES = {
  poles: {
    label: "Słupy", singular: "Słup", icon: CircleDot, geo: "point", marker: "#EAB308",
    display: "number",
    fields: [
      { name: "number", label: "Numer słupa", required: true },
      { name: "ewid_number", label: "Numer ewidencyjny" },
      { name: "owner", label: "Właściciel" },
      { name: "pole_type", label: "Typ słupa", type: "select", options: ["wirowany", "żerdź E", "drewniany", "stalowy"] },
      { name: "material", label: "Materiał", type: "select", options: ["beton", "drewno", "stal", "kompozyt"] },
      { name: "height", label: "Wysokość [m]", type: "number" },
      { name: "condition", label: "Stan techniczny", type: "select", options: ["dobry", "dostateczny", "zły"] },
      { name: "notes", label: "Notatki", type: "textarea" },
    ],
  },
  routes: {
    label: "Trasy kablowe", singular: "Trasa", icon: Waypoints, geo: "line", marker: "#06B6D4",
    display: "name",
    fields: [
      { name: "name", label: "Nazwa trasy", required: true },
      { name: "route_type", label: "Typ trasy", type: "select", options: ["napowietrzna", "kanalizacja", "mikrokanalizacja", "w ziemi", "HDPE", "RHDPE"] },
      { name: "length_m", label: "Długość [m]", type: "number" },
      { name: "diameter", label: "Średnica rur" },
      { name: "tube_count", label: "Liczba rur", type: "number" },
      { name: "tubes_occupied", label: "Zajęte mikrorurki", type: "number" },
      { name: "microtubes_free", label: "Wolne mikrorurki", type: "number" },
      { name: "notes", label: "Notatki", type: "textarea" },
    ],
  },
  cables: {
    label: "Kable", singular: "Kabel", icon: Cable, geo: "line", marker: "#3B82F6",
    display: "number",
    fields: [
      { name: "number", label: "Numer kabla", required: true },
      { name: "producent", label: "Producent" },
      { name: "catalog_id", label: "Model z katalogu", type: "ref", ref: "cable_catalog" },
      { name: "cable_type", label: "Typ kabla" },
      { name: "miejscowosc", label: "Miejscowość" },
      { name: "gmina", label: "Gmina" },
      { name: "placement", label: "Umieszczenie (kolor na mapie)", type: "select", options: ["napowietrzny", "kanalizacja", "kanalizacja OPL", "kanalizacja własna", "mikrokanalizacja", "doziemny"] },
      { name: "uke_line_kind", label: "Rodzaj linii kablowej (UKE) — puste = auto z „Umieszczenie”", type: "select", options: ["", "Linia kablowa podziemna (umieszczona bezpośrednio w ziemi)", "Linia kablowa umieszczona w kanalizacji kablowej (w tym w rurociągu kablowym, mikrokanalizacji)", "Linia kablowa umieszczona w kanale technologicznym", "Linia kablowa nadziemna na podbudowie słupowej telekomunikacyjnej", "Linia kablowa nadziemna na podbudowie elektroenergetycznej, oświetleniowej lub trakcyjnej", "Linia kablowa nadziemna zainstalowana między budynkami"] },
      { name: "fiber_count", label: "Liczba włókien", type: "number", default: 12 },
      { name: "tube_count", label: "Liczba tub", type: "number", default: 1 },
      { name: "fibers_per_tube", label: "Włókien w tubie (1–24)", type: "number", default: 12 },
      { name: "fiber_standard", label: "Standard kolorów włókien", type: "select", optionsKey: "fiber_standards" },
      { name: "closure_a_id", label: "Mufa A (koniec)", type: "ref", ref: "closures" },
      { name: "closure_b_id", label: "Mufa B (koniec)", type: "ref", ref: "closures" },
      { name: "atten_1310", label: "Tłumienie 1310nm [dB/km]", type: "number", default: 0.35 },
      { name: "atten_1550", label: "Tłumienie 1550nm [dB/km]", type: "number", default: 0.22 },
      { name: "length_m", label: "Długość [m]", type: "number" },
      { name: "microduct_id", label: "Mikrokanalizacja (trasa)", type: "ref", ref: "microducts" },
      { name: "microduct_tube", label: "Nr mikrorury", type: "number" },
      { name: "pipeline_id", label: "Rurociąg (trasa)", type: "ref", ref: "pipelines" },
      { name: "duct_id", label: "Kanalizacja (trasa)", type: "ref", ref: "ducts" },
      { name: "diameter_mm", label: "Średnica kabla [mm]", type: "number" },
      { name: "from_point", label: "Początek" },
      { name: "to_point", label: "Koniec" },
      { name: "status", label: "Status", type: "select", options: ["aktywny", "uszkodzony", "projektowany"] },
    ],
  },
  closures: {
    label: "Mufy", singular: "Mufa", icon: Boxes, geo: "point", marker: "#A855F7",
    display: "number",
    fields: [
      { name: "number", label: "Numer mufy", required: true },
      { name: "pole_number", label: "Na słupie (nr)", type: "text" },
      { name: "closure_type", label: "Typ", type: "select", options: ["kopułowa", "przelotowa", "leżakowa", "naścienna"] },
      { name: "producent", label: "Producent" },
      { name: "catalog_id", label: "Model z katalogu", type: "ref", ref: "closure_catalog" },
      { name: "tray_count", label: "Ilość tacek", type: "number" },
      { name: "tray_capacity", label: "Spawów na tacce", type: "number", default: 12 },
      { name: "splice_capacity", label: "Pojemność spawów (mufa)", type: "number" },
      { name: "inputs_count", label: "Liczba wejść", type: "number" },
      { name: "outputs_count", label: "Liczba wyjść", type: "number" },
      { name: "uncut_port", label: "Port uncut", type: "select", options: ["Tak", "Nie"] },
      { name: "description", label: "Opis", type: "textarea" },
    ],
  },
  splices: {
    label: "Spawy", singular: "Spaw", icon: GitFork, geo: "none", marker: "#EC4899",
    display: "splice_no",
    displayFn: (it) => `Spaw ${it.splice_no ?? "?"}${it.color ? ` (${it.color})` : ""}`,
    fields: [
      { name: "closure_id", label: "Mufa", type: "ref", ref: "closures" },
      { name: "target_type", label: "Cel spawu", type: "select", options: ["Włókno", "Splitter"], default: "Włókno" },
      { name: "target_splitter_id", label: "Splitter (jeśli cel = Splitter)", type: "ref", ref: "splitters" },
      { name: "cable_id", label: "Kabel (włókno wejściowe)", type: "ref", ref: "cables" },
      { name: "tray_no", label: "Nr tacki", type: "number" },
      { name: "splice_no", label: "Nr spawu", type: "number" },
      { name: "fiber_in", label: "Włókno wejściowe", type: "number" },
      { name: "fiber_out", label: "Włókno wyjściowe", type: "number" },
      { name: "color", label: "Kolor", type: "select", options: FIBER_COLORS },
      { name: "attenuation_db", label: "Tłumienie [dB]", type: "number" },
      { name: "date", label: "Data wykonania", type: "date" },
      { name: "technician", label: "Monter", type: "ref", ref: "technicians" },
    ],
  },
  splitters: {
    label: "Splittery", singular: "Splitter", icon: Split, geo: "point", marker: "#F97316",
    display: "number",
    fields: [
      { name: "number", label: "Numer", required: true },
      { name: "pole_number", label: "Na słupie (nr)", type: "text" },
      { name: "split_type", label: "Typ", type: "select", options: ["1:2", "1:4", "1:8", "1:16", "1:32", "1:64"] },
      { name: "producent", label: "Producent" },
      { name: "closure_id", label: "Mufa", type: "ref", ref: "closures" },
      { name: "olt_id", label: "OLT", type: "ref", ref: "olt" },
      { name: "input_source", label: "Źródło sygnału", type: "select", options: ["Kabel", "Splitter (kaskada)"], default: "Kabel" },
      { name: "input_cable_id", label: "Kabel wejściowy (feeder)", type: "ref", ref: "cables" },
      { name: "input_fiber_number", label: "Włókno wejściowe (z kabla)", type: "number" },
      { name: "parent_splitter_id", label: "Splitter nadrzędny (kaskada)", type: "ref", ref: "splitters" },
      { name: "parent_port", label: "Port splittera nadrzędnego", type: "number" },
      { name: "cable_id", label: "Kabel wyjściowy", type: "ref", ref: "cables" },
      { name: "pon_port", label: "Port PON" },
      { name: "ports_occupied", label: "Zajęte porty", type: "number" },
      { name: "input_power_dbm", label: "Moc optyczna [dBm]", type: "number" },
      { name: "connector_type", label: "Typ złącza", type: "select", options: ["UPC", "APC"] },
      { name: "connector_atten_db", label: "Tłumienie złączy/adapterów [dB]", type: "number" },
      { name: "location", label: "Lokalizacja" },
    ],
  },
  odf: {
    label: "Szafy", singular: "Szafa", icon: Server, geo: "point", marker: "#22C55E",
    display: "number",
    fields: [
      { name: "number", label: "Numer", required: true },
      { name: "location", label: "Lokalizacja" },
      { name: "catalog_id", label: "Model z katalogu", type: "ref", ref: "odf_catalog" },
      { name: "cabinet_type", label: "Typ szafy", type: "select", options: ["Rack 19\"", "Naścienna", "Wolnostojąca", "Uliczna", "Wewnętrzna"] },
      { name: "height_u", label: "Wysokość [U]", type: "number" },
      { name: "panel_count", label: "Ilość paneli", type: "number" },
      { name: "port_count", label: "Ilość portów", type: "number" },
      { name: "has_ac", label: "Klimatyzacja", type: "select", options: ["Tak", "Nie"] },
      { name: "power_info", label: "Zasilanie" },
      { name: "camera_url", label: "Strumień kamery IP (MJPEG/HLS/MP4/snapshot)", type: "text" },
      { name: "rtsp_url", label: "Strumień RTSP (na żywo — przez bramkę serwera)", type: "text" },
      { name: "notes", label: "Notatki", type: "textarea" },
    ],
  },
  olt: {
    label: "OLT", singular: "OLT", icon: RadioTower, geo: "point", marker: "#3B82F6",
    display: "name",
    fields: [
      { name: "name", label: "Nazwa", required: true },
      { name: "pole_number", label: "Na słupie (nr)", type: "text" },
      { name: "producent", label: "Producent" },
      { name: "model", label: "Model" },
      { name: "ip_address", label: "Adres IP" },
      { name: "slots", label: "Liczba slotów", type: "number" },
      { name: "pon_ports_total", label: "Porty PON (razem)", type: "number" },
      { name: "pon_ports_occupied", label: "Porty PON zajęte", type: "number" },
      { name: "sfp_ports", label: "Porty SFP/SFP+", type: "number" },
      { name: "ethernet_ports", label: "Porty Ethernet", type: "number" },
      { name: "install_date", label: "Data montażu / instalacji", type: "date" },
      { name: "warranty_until", label: "Gwarancja do", type: "date" },
      { name: "notes", label: "Notatki", type: "textarea" },
    ],
  },
  olt_cards: {
    label: "Wkładki OLT", singular: "Wkładka OLT", icon: Cpu, geo: "none", marker: "#3B82F6",
    display: "number",
    fields: [
      { name: "number", label: "Slot / Wkładka", required: true },
      { name: "olt_id", label: "OLT", type: "ref", ref: "olt" },
      { name: "tx_power_dbm", label: "Moc nadawcza [dBm]", type: "number" },
      { name: "pon_ports", label: "Porty PON", type: "number" },
      { name: "notes", label: "Notatki", type: "textarea" },
    ],
  },
  onu: {
    label: "ONU / ONT", singular: "ONU", icon: Router, geo: "point", marker: "#06B6D4",    display: "serial",
    fields: [
      { name: "serial", label: "Numer seryjny", required: true },
      { name: "mac", label: "Adres MAC" },
      { name: "model", label: "Model" },
      { name: "producent", label: "Producent" },
      { name: "status", label: "Status", type: "select", options: ["online", "offline"] },
      { name: "pon_port", label: "Port PON" },
      { name: "client_id", label: "Klient", type: "ref", ref: "clients" },
      { name: "install_date", label: "Data montażu / instalacji", type: "date" },
      { name: "warranty_until", label: "Gwarancja do", type: "date" },
      { name: "notes", label: "Notatki", type: "textarea" },
    ],
  },
  client_cables: {
    label: "Kable klienckie", singular: "Kabel kliencki", icon: Cable, geo: "line", marker: "#F472B6",
    display: "number",
    fields: [
      { name: "number", label: "Numer / etykieta", required: true },
      { name: "client_id", label: "Klient", type: "ref", ref: "clients" },
      { name: "splitter_id", label: "Splitter", type: "ref", ref: "splitters" },
      { name: "splitter_port", label: "Port splittera", type: "number" },
      { name: "fiber_number", label: "Nr włókna", type: "number" },
      { name: "fiber_color", label: "Kolor włókna", type: "select", options: FIBER_COLORS },
      { name: "cable_type", label: "Typ kabla", type: "text" },
      { name: "length_m", label: "Długość [m]", type: "number" },
      { name: "atten_1310", label: "Tłumienie 1310nm [dB/km]", type: "number", default: 0.35 },
      { name: "atten_1550", label: "Tłumienie 1550nm [dB/km]", type: "number", default: 0.22 },
    ],
  },

  clients: {
    label: "Klienci", singular: "Klient", icon: Users, geo: "point", marker: "#EF4444",
    display: "name",
    fields: [
      { name: "name", label: "Nazwa / Imię i nazwisko", required: true, custom: true },
      { name: "address", label: "Adres", custom: true },
      { name: "client_type", label: "Typ klienta", default: "osoba", custom: true },
      { name: "first_name", label: "Imię", custom: true },
      { name: "last_name", label: "Nazwisko", custom: true },
      { name: "company_name", label: "Nazwa firmy", custom: true },
      { name: "nip", label: "NIP", custom: true },
      { name: "pesel", label: "PESEL", custom: true },
      { name: "id_number", label: "Nr dowodu osobistego", custom: true },
      { name: "city", label: "Miejscowość", custom: true },
      { name: "street", label: "Ulica", custom: true },
      { name: "house_no", label: "Nr budynku", custom: true },
      { name: "postal_code", label: "Kod pocztowy", custom: true },
      { name: "teryt_terc", label: "TERC", custom: true },
      { name: "teryt_simc", label: "SIMC", custom: true },
      { name: "teryt_ulic", label: "ULIC", custom: true },
      { name: "contract_no", label: "Numer umowy" },
      { name: "uke_service_id", label: "Identyfikator usługi (UKE) — puste = auto (bez danych osobowych)", type: "text" },
      { name: "contract_period", label: "Okres umowy (np. 24 mies.)" },
      { name: "service_start", label: "Start usługi", type: "date" },
      { name: "service_end", label: "Zakończenie usługi", type: "date" },
      { name: "phone", label: "Telefon", custom: true },
      { name: "email", label: "Email (logowanie do app klienta)" },
      { name: "pin_set_at", label: "PIN do app klienta ustawiony", readonly: true },
      { name: "status", label: "Status", type: "select", options: ["aktywny", "zawieszony", "projektowany", "potencjał"] },
      { name: "connection_type", label: "Typ połączenia", type: "select", options: ["DHCP", "PPPoE", "IP statyczne", "Inne"] },
      { name: "pppoe_login", label: "Login PPPoE" },
      { name: "pppoe_password", label: "Hasło PPPoE" },
      { name: "speed_plan", label: "Plan prędkości", type: "select", optionsKey: "speed_plans" },
      { name: "download_mbps", label: "Download [Mb/s]", type: "number" },
      { name: "upload_mbps", label: "Upload [Mb/s]", type: "number" },
      { name: "ip_address", label: "Adres IP" },
      { name: "bank_account", label: "Nr konta do wpłat" },
      { name: "technology", label: "Technologia", type: "select", optionsKey: "client_technologies" },
      { name: "voip", label: "VoIP", type: "select", options: ["Nie", "Tak"] },
      { name: "iptv", label: "IPTV", type: "select", options: ["Nie", "Tak"] },
      { name: "iptv_plan", label: "Pakiet IPTV", type: "select", optionsKey: "iptv_plans" },
      { name: "voip_tariff", label: "Taryfa VoIP", type: "select", optionsKey: "voip_tariffs" },
      { name: "mobile_plan", label: "Plan komórkowy", type: "select", optionsKey: "mobile_plans" },
      { name: "power_measurement_dbm", label: "Pomiar mocy [dBm]", type: "number" },
      { name: "olt_id", label: "OLT", type: "ref", ref: "olt" },
      { name: "card", label: "Karta", type: "number" },
      { name: "pon_port", label: "Port PON" },
      { name: "onu_id", label: "ID / indeks ONU (SNMP)" },
      { name: "onu_sn", label: "S/N ONU" },
      { name: "onu_mac", label: "MAC ONU" },
      { name: "onu_lan_mac", label: "MAC na porcie Ethernet ONU (klient)" },
      { name: "onu_tx_dbm", label: "Moc Tx ONU [dBm]", type: "number" },
      { name: "onu_temp_c", label: "Temperatura ONU [°C]", type: "number" },
      { name: "onu_distance_m", label: "Odległość ONU [m]", type: "number" },
      { name: "onu_model", label: "Model ONU" },
      { name: "onu_status", label: "Status ONU" },
      { name: "splitter_id", label: "Splitter", type: "ref", ref: "splitters" },
      { name: "splitter_port", label: "Port splittera", type: "number" },
      { name: "fiber_number", label: "Nr włókna", type: "number" },
      { name: "fiber_color", label: "Kolor włókna", type: "select", options: FIBER_COLORS },
    ],
  },
  faults: {
    label: "Awarie", singular: "Awaria", icon: TriangleAlert, geo: "point", marker: "#EF4444",
    display: "title",
    fields: [
      { name: "title", label: "Tytuł", required: true },
      { name: "description", label: "Opis", type: "textarea" },
      { name: "priority", label: "Priorytet", type: "select", options: ["niski", "średni", "wysoki", "krytyczny"] },
      { name: "status", label: "Status", type: "select", options: ["nowa", "w toku", "usunięta"] },
      { name: "reported_at", label: "Zgłoszono", type: "date" },
      { name: "deadline", label: "Termin usunięcia", type: "date" },
    ],
  },
  documents: {
    label: "Dokumentacja", singular: "Dokument", icon: FileText, geo: "none", marker: "#94A3B8",
    display: "name",
    fields: [
      { name: "name", label: "Nazwa", required: true },
      { name: "doc_type", label: "Typ", type: "select", options: ["PDF", "Zdjęcie", "Protokół", "OTDR", "Pomiar mocy", "Instrukcja", "Gwarancja"] },
      { name: "url", label: "Link / URL" },
      { name: "entity_type", label: "Powiązany moduł" },
      { name: "entity_id", label: "ID obiektu" },
      { name: "notes", label: "Notatki", type: "textarea" },
    ],
  },
  cable_catalog: {
    label: "Katalog kabli", singular: "Kabel (katalog)", icon: Library, geo: "none", marker: "#3B82F6",
    display: "model", catalog: true,
    fields: [
      { name: "model", label: "Model / Nazwa", required: true },
      { name: "producent", label: "Producent" },
      { name: "cable_type", label: "Typ kabla" },
      { name: "fiber_count", label: "Liczba włókien", type: "number" },
      { name: "diameter_mm", label: "Średnica [mm]", type: "number" },
      { name: "weight_kg_km", label: "Waga [kg/km]", type: "number" },
      { name: "min_bend_mm", label: "Min. promień gięcia [mm]", type: "number" },
      { name: "tensile_n", label: "Wytrzymałość [N]", type: "number" },
      { name: "color", label: "Kolor powłoki" },
      { name: "application", label: "Zastosowanie", type: "select", options: ["napowietrzny", "kanalizacyjny", "ziemny", "ADSS", "uniwersalny", "wewnętrzny"] },
      { name: "notes", label: "Uwagi", type: "textarea" },
    ],
  },
  closure_catalog: {
    label: "Katalog muf", singular: "Mufa (katalog)", icon: Package, geo: "none", marker: "#A855F7",
    display: "model", catalog: true,
    fields: [
      { name: "model", label: "Model / Nazwa", required: true },
      { name: "producent", label: "Producent" },
      { name: "closure_type", label: "Typ mufy", type: "select", options: ["kopułowa", "przelotowa", "leżakowa", "naścienna"] },
      { name: "tray_count", label: "Liczba tacek", type: "number" },
      { name: "max_splices", label: "Maks. spawów", type: "number" },
      { name: "capacity", label: "Pojemność (włókna)", type: "number" },
      { name: "sealing", label: "Uszczelnienie", type: "select", options: ["termokurczliwe", "mechaniczne", "żelowe"] },
      { name: "dimensions", label: "Wymiary" },
      { name: "notes", label: "Uwagi", type: "textarea" },
    ],
  },
  odf_catalog: {
    label: "Katalog szaf/ODF", singular: "Szafa (katalog)", icon: Warehouse, geo: "none", marker: "#22C55E",
    display: "model", catalog: true,
    fields: [
      { name: "model", label: "Model / Nazwa", required: true },
      { name: "producent", label: "Producent" },
      { name: "cabinet_type", label: "Typ szafy", type: "select", options: ["Rack 19\"", "Naścienna", "Wolnostojąca", "Uliczna", "Wewnętrzna"] },
      { name: "height_u", label: "Wysokość [U]", type: "number" },
      { name: "port_count", label: "Maks. portów", type: "number" },
      { name: "dimensions", label: "Wymiary" },
      { name: "notes", label: "Uwagi", type: "textarea" },
    ],
  },
  technicians: {
    label: "Monterzy", singular: "Monter", icon: HardHat, geo: "none", marker: "#EAB308",
    display: "name",
    fields: [
      { name: "name", label: "Imię i nazwisko", required: true },
      { name: "phone", label: "Telefon" },
      { name: "company", label: "Firma" },
      { name: "email", label: "Email" },
    ],
  },
};

MODULES.patchpanels = {
  label: "Przełącznice / ODF", singular: "Przełącznica", icon: Rows3, geo: "point", marker: "#10B981",
  display: "number",
  fields: [
    { name: "number", label: "Numer / etykieta", required: true },
    { name: "location", label: "Lokalizacja" },
    { name: "cabinet_id", label: "Szafa (rack)", type: "ref", ref: "odf" },
    { name: "rack_u", label: "Pozycja U w szafie", type: "number" },
    { name: "size_u", label: "Rozmiar [U]", type: "number", default: 1 },
    { name: "port_count", label: "Liczba portów", type: "number", default: 24 },
    { name: "adapter_type", label: "Typ adaptera", type: "select", options: ["SC/UPC", "SC/APC", "LC/UPC", "LC/APC", "FC/UPC", "FC/APC", "E2000/APC"] },
    { name: "tray_count", label: "Liczba tacek", type: "number" },
    { name: "input_count", label: "Liczba wejść (kabli)", type: "number" },
    { name: "producent", label: "Producent" },
    { name: "catalog_id", label: "Model z katalogu", type: "ref", ref: "odf_catalog" },
    { name: "notes", label: "Notatki", type: "textarea" },
  ],
};

MODULES.coverage = {
  label: "Zasięgi Sieci", singular: "Zasięg", icon: Radar, geo: "point", marker: "#14B8A6",
  display: "identyfikator",
  fields: [
    { name: "identyfikator", label: "Identyfikator zasięgu (auto)" },
    { name: "miejscowosc", label: "Miejscowość", required: true },
    { name: "wojewodztwo", label: "Województwo" },
    { name: "powiat", label: "Powiat" },
    { name: "gmina", label: "Gmina" },
    { name: "simc", label: "Kod SIMC miejscowości" },
    { name: "ulica", label: "Ulica" },
    { name: "kod_ulic", label: "Kod ULIC ulicy" },
    { name: "nr_budynku", label: "Nr budynku" },
    { name: "medium", label: "Medium transmisyjne", type: "select", optionsKey: "coverage_media" },
    { name: "technologia", label: "Technologia dostępowa", type: "select", optionsKey: "coverage_technologies" },
    { name: "przedstawiciel", label: "Przedstawiciel" },
    { name: "rodzaj_uslugi", label: "Rodzaj usługi", type: "checkboxes", options: ["Usługa hurtowa", "Usługa detaliczna"] },
    { name: "infrastruktura_wlasna", label: "Możliwość dostarczenia usługi na infrastrukturze własnej", type: "radio", options: ["tak", "nie"] },
    { name: "downlink_mbps", label: "Maks. przepustowość — Downlink (Mb/s)", type: "select", optionsKey: "coverage_bandwidths" },
    { name: "uplink_mbps", label: "Maks. przepustowość — Uplink (Mb/s)", type: "select", optionsKey: "coverage_bandwidths" },
    { name: "rodzaj_zasiegu", label: "Rodzaj zasięgu", type: "radio", options: ["rzeczywisty", "teoretyczny"] },
  ],
};

MODULES.ducts = {
  label: "Kanalizacja", singular: "Kanalizacja", icon: Boxes, geo: "line", marker: "#8B5CF6",
  display: "name",
  fields: [
    { name: "name", label: "Nazwa / oznaczenie", required: true },
    { name: "duct_type", label: "Typ", type: "select", options: ["pierwotna", "wtórna", "teletechniczna"] },
    { name: "diameter", label: "Średnica [mm]" },
    { name: "inner_diameter_mm", label: "Światło rury (wewn.) [mm]", type: "number" },
    { name: "notes", label: "Notatki", type: "textarea" },
  ],
};
MODULES.wells = {
  label: "Studnie", singular: "Studnia", icon: CircleDot, geo: "point", marker: "#0EA5E9",
  display: "number",
  fields: [
    { name: "number", label: "Numer studni", required: true },
    { name: "well_type", label: "Typ", type: "select", options: ["SK-1", "SK-2", "SKR-1", "SKR-2", "kablowa", "rozdzielcza"] },
    { name: "material", label: "Materiał", type: "select", options: ["beton", "polimerobeton", "tworzywo"] },
    { name: "cover_class", label: "Klasa włazu", type: "select", options: ["A15", "B125", "C250", "D400"] },
    { name: "notes", label: "Notatki", type: "textarea" },
  ],
};
MODULES.microducts = {
  label: "Mikrokanalizacja", singular: "Mikrokanalizacja", icon: Split, geo: "line", marker: "#F59E0B",
  display: "name",
  fields: [
    { name: "name", label: "Nazwa / oznaczenie", required: true },
    { name: "config", label: "Konfiguracja wiązki" },
    { name: "tube_count", label: "Liczba mikrorur", type: "number" },
    { name: "tubes_occupied", label: "Zajęte mikrorury", type: "number" },
    { name: "diameter", label: "Średnica mikrorur [mm]" },
    { name: "length_m", label: "Długość [m]", type: "number" },
    { name: "notes", label: "Notatki", type: "textarea" },
  ],
};
MODULES.pipelines = {
  label: "Rurociągi", singular: "Rurociąg", icon: GitFork, geo: "line", marker: "#10B981",
  display: "name",
  fields: [
    { name: "name", label: "Nazwa / oznaczenie", required: true },
    { name: "pipe_type", label: "Typ rurociągu", type: "select", options: ["przewiert", "wykop", "napowietrzny", "podwodny"] },
    { name: "diameter", label: "Średnica [mm]" },
    { name: "length_m", label: "Długość [m]", type: "number" },
    { name: "depth_m", label: "Głębokość [m]", type: "number" },
    { name: "notes", label: "Notatki", type: "textarea" },
  ],
};

MODULES.switches = {
  label: "Switche", singular: "Switch", icon: Network, geo: "point", marker: "#F59E0B",
  display: "name",
  fields: [
    { name: "name", label: "Nazwa", required: true },
    { name: "pole_number", label: "Na słupie (nr)", type: "text" },
    { name: "location", label: "Lokalizacja" },
    { name: "producent", label: "Producent" },
    { name: "model", label: "Model" },
    { name: "ip_address", label: "Adres IP" },
    { name: "mgmt_vlan", label: "VLAN zarządzania" },
    { name: "rj45_ports", label: "Porty RJ45", type: "number", default: 24 },
    { name: "sfp_ports", label: "Porty SFP/SFP+", type: "number", default: 4 },
    { name: "poe", label: "PoE", type: "select", options: ["Nie", "Tak"] },
    { name: "uplink", label: "Uplink (do czego)" },
    { name: "install_date", label: "Data montażu / instalacji", type: "date" },
    { name: "warranty_until", label: "Gwarancja do", type: "date" },
    { name: "notes", label: "Notatki", type: "textarea" },
  ],
};

MODULES.zones = {
  label: "Strefy / zasięgi", singular: "Strefa", icon: RadioTower, geo: "polygon", marker: "#22C55E",
  display: "name",
  fields: [
    { name: "name", label: "Nazwa strefy", required: true },
    { name: "technology", label: "Technologia", type: "select", optionsKey: "coverage_technologies" },
    { name: "medium", label: "Medium", type: "select", optionsKey: "coverage_media" },
    { name: "bandwidth", label: "Przepustowość [Mb/s]", type: "select", optionsKey: "coverage_bandwidths" },
    { name: "miejscowosc", label: "Miejscowość" },
    { name: "gmina", label: "Gmina" },
    { name: "notes", label: "Notatki", type: "textarea" },
  ],
};

MODULES.cable_slack = {
  label: "Zapasy kabla", singular: "Zapas kabla", icon: Cable, geo: "point", marker: "#F43F5E",
  display: "cable_number",
  displayFn: (it) => `${it.cable_number || it.cable || "?"}${it.length_m != null ? ` — ${it.length_m} m` : ""}`,
  fields: [
    { name: "cable", label: "Kabel", type: "ref", ref: "cables", required: true },
    { name: "cable_number", label: "Numer kabla", readonly: true },
    { name: "length_m", label: "Długość zapasu [m]", type: "number", required: true },
    { name: "reserve_type", label: "Typ zapasu", type: "select", options: ["Słup", "Studnia", "Mufa", "Szafa", "Inny"] },
    { name: "note", label: "Opis / lokalizacja", type: "textarea" },
  ],
};

export const MODULE_ORDER = ["poles", "routes", "ducts", "wells", "microducts", "pipelines",
  "cables", "cable_slack", "client_cables", "closures", "splices",
  "splitters", "odf", "patchpanels", "olt", "olt_cards", "switches", "onu", "clients", "coverage", "zones", "faults", "documents",
  "technicians", "cable_catalog", "closure_catalog", "odf_catalog"];

export { Zap, MapPin };
